import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { toast } from 'sonner';
import { Check, Package, Ban, Search, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

const fmt = (n, cur = 'NZD') => `$${Number(n || 0).toFixed(2)} ${cur}`;

const STATUS_ORDER = ['pending', 'paid', 'shipped', 'cancelled'];

export default function AdminFounderOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});
  const [shipDlg, setShipDlg] = useState(null); // order being shipped
  const [ship, setShip] = useState({ courier: '', tracking: '' });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/founder-orders');
      setOrders(data.orders || []);
    } catch {
      toast.error('Failed to load orders');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return orders.filter((o) => {
      if (filter !== 'all' && o.status !== filter) return false;
      if (!q.trim()) return true;
      const s = q.toLowerCase();
      return (
        (o.invoice_number || '').toLowerCase().includes(s)
        || (o.user_name || '').toLowerCase().includes(s)
        || (o.user_email || '').toLowerCase().includes(s)
        || (o.founder_number || '').toLowerCase().includes(s)
      );
    });
  }, [orders, filter, q]);

  const counts = useMemo(() => {
    const c = { all: orders.length };
    STATUS_ORDER.forEach((s) => { c[s] = orders.filter((o) => o.status === s).length; });
    return c;
  }, [orders]);

  const markPaid = async (o) => {
    if (!confirm(`Mark ${o.invoice_number} as PAID?`)) return;
    try {
      await api.post('/admin/founder-orders/mark-paid', { order_id: o.id });
      toast.success(`${o.invoice_number} marked paid`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const openShip = (o) => { setShipDlg(o); setShip({ courier: '', tracking: '' }); };

  const doShip = async () => {
    try {
      await api.post('/admin/founder-orders/mark-shipped', { order_id: shipDlg.id, courier: ship.courier, tracking: ship.tracking });
      toast.success(`${shipDlg.invoice_number} marked shipped`);
      setShipDlg(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const cancel = async (o) => {
    if (!confirm(`Cancel ${o.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.post('/admin/founder-orders/cancel', { order_id: o.id });
      toast.success('Cancelled');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const remove = async (o) => {
    if (!confirm(`Delete ${o.invoice_number} permanently? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/founder-orders/${o.id}`);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  const toggleExpanded = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  return (
    <div className="space-y-5" data-testid="admin-founder-orders">
      <div className="h-panel-thin p-6">
        <div className="h-eyebrow">Store</div>
        <div className="h-display text-4xl mt-1" style={{ color: 'var(--heritage-plum)' }}>Founder Orders</div>
        <p className="text-sm opacity-85 mt-1" style={{ color: 'var(--heritage-plum)' }}>Add-on orders from founders. Confirm bank deposits and dispatch here.</p>
      </div>

      <Panel className="p-4">
        <div className="flex flex-wrap items-center gap-3 justify-between">
          <div className="flex flex-wrap items-center gap-2">
            {['all', ...STATUS_ORDER].map((s) => (
              <button key={s} onClick={() => setFilter(s)}
                className="rounded-full px-3 py-1 text-xs h-serif transition-colors"
                style={{
                  background: filter === s ? 'var(--heritage-plum)' : 'transparent',
                  color: filter === s ? 'var(--heritage-cream)' : 'var(--heritage-plum)',
                  border: '1px solid var(--heritage-plum)',
                }}
                data-testid={`orders-filter-${s}`}
              >
                {s === 'all' ? 'All' : s[0].toUpperCase() + s.slice(1)} <span className="opacity-70">({counts[s] || 0})</span>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-2.5 opacity-60" style={{ color: 'var(--heritage-plum)' }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search invoice / email / name"
              className="pl-8 pr-3 py-1.5 rounded-full text-sm" style={{ border: '1px solid rgba(75,24,56,0.3)', background: '#FBF8F1', color: 'var(--heritage-plum)', minWidth: 260 }}
              data-testid="orders-search" />
          </div>
        </div>
      </Panel>

      {loading && <div className="h-eyebrow">Loading...</div>}

      {!loading && filtered.length === 0 && (
        <Panel className="p-8 text-center">
          <div className="h-eyebrow">No orders</div>
          <div className="h-serif text-xl mt-2" style={{ color: 'var(--heritage-plum)' }}>Nothing to show for this filter.</div>
        </Panel>
      )}

      <div className="space-y-3">
        {filtered.map((o) => {
          const isOpen = !!expanded[o.id];
          return (
            <Panel key={o.id} className="p-4" data-testid={`admin-order-${o.invoice_number}`}>
              <div className="flex flex-wrap items-center gap-3 justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="h-display text-xl" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</span>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>
                    {o.user_name} • {o.user_email} {o.founder_number ? ` • ${o.founder_number}` : ''}
                  </div>
                  <div className="text-xs opacity-75" style={{ color: 'var(--heritage-plum)' }}>
                    {new Date(o.created_at).toLocaleString('en-NZ')} • {o.items.length} item{o.items.length === 1 ? '' : 's'} • <b>{fmt(o.total, o.currency)}</b>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {o.status === 'pending' && (
                    <button onClick={() => markPaid(o)} className="h-btn text-xs" data-testid={`order-mark-paid-${o.invoice_number}`}>
                      <Check size={12} className="mr-1" /> Mark paid
                    </button>
                  )}
                  {o.status === 'paid' && (
                    <button onClick={() => openShip(o)} className="h-btn text-xs" data-testid={`order-ship-${o.invoice_number}`}>
                      <Package size={12} className="mr-1" /> Mark shipped
                    </button>
                  )}
                  {o.status !== 'cancelled' && o.status !== 'shipped' && (
                    <button onClick={() => cancel(o)} className="h-btn-outline text-xs" style={{ borderColor: '#9A2C2C', color: '#9A2C2C' }} data-testid={`order-cancel-${o.invoice_number}`}>
                      <Ban size={12} className="mr-1" /> Cancel
                    </button>
                  )}
                  <button onClick={() => remove(o)} className="text-xs inline-flex items-center gap-1 opacity-80 hover:opacity-100 px-2 py-1" style={{ color: '#9A2C2C' }} data-testid={`order-delete-${o.invoice_number}`} title="Delete permanently">
                    <Trash2 size={12} /> Delete
                  </button>
                  <button onClick={() => toggleExpanded(o.id)} className="h-btn-outline text-xs">
                    {isOpen ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                  </button>
                </div>
              </div>
              {isOpen && (
                <div className="mt-3 pt-3 border-t" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <div className="h-eyebrow text-[10px]">Items</div>
                      <ul className="mt-2 text-sm space-y-1" style={{ color: 'var(--heritage-plum)' }}>
                        {o.items.map((li) => (
                          <li key={li.slug} className="flex items-center justify-between gap-3">
                            <span>{li.qty}× <span className="h-serif">{li.name}</span> <span className="opacity-70 text-xs">{li.colourway_swatch}</span></span>
                            <span className="h-serif">${Number(li.line_total).toFixed(2)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="h-eyebrow text-[10px]">Delivery</div>
                      <div className="text-sm whitespace-pre-line mt-2" style={{ color: 'var(--heritage-plum)' }}>{o.delivery_address || 'No override — ship with bundle.'}</div>
                      {o.delivery_notes && <div className="text-xs opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Notes: {o.delivery_notes}</div>}
                      <div className="h-eyebrow text-[10px] mt-3">Payment reference</div>
                      <div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{o.invoice_number}</div>
                      {o.status === 'shipped' && (
                        <>
                          <div className="h-eyebrow text-[10px] mt-3">Tracking</div>
                          <div className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{o.shipping_courier} {o.shipping_tracking}</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Panel>
          );
        })}
      </div>

      {shipDlg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(75,24,56,0.5)' }} onClick={() => setShipDlg(null)}>
          <div className="w-full max-w-md rounded-2xl p-6" onClick={(e) => e.stopPropagation()} style={{ background: '#FBF8F1', border: '3px solid var(--heritage-plum)' }} data-testid="admin-ship-dialog">
            <div className="h-eyebrow">Mark shipped</div>
            <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{shipDlg.invoice_number}</div>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>Courier</label>
                <input value={ship.courier} onChange={(e) => setShip({ ...ship, courier: e.target.value })} placeholder="NZ Post, Aramex, ..."
                  className="w-full rounded-md px-2 py-1.5 text-sm mt-1" style={{ border: '1px solid rgba(75,24,56,0.3)', color: 'var(--heritage-plum)', background: '#FBF8F1' }} data-testid="ship-courier" />
              </div>
              <div>
                <label className="block text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>Tracking number</label>
                <input value={ship.tracking} onChange={(e) => setShip({ ...ship, tracking: e.target.value })} placeholder="Optional"
                  className="w-full rounded-md px-2 py-1.5 text-sm mt-1" style={{ border: '1px solid rgba(75,24,56,0.3)', color: 'var(--heritage-plum)', background: '#FBF8F1' }} data-testid="ship-tracking" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShipDlg(null)} className="h-btn-outline text-xs" data-testid="ship-cancel">Cancel</button>
              <button onClick={doShip} className="h-btn text-xs" data-testid="ship-confirm">Mark shipped</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pending: { label: 'Pending', bg: 'rgba(75,24,56,0.08)' },
    paid: { label: 'Paid', bg: 'rgba(91,110,44,0.15)' },
    shipped: { label: 'Shipped', bg: 'rgba(75,24,56,0.2)' },
    cancelled: { label: 'Cancelled', bg: 'rgba(154,44,44,0.15)' },
  };
  const s = map[status] || map.pending;
  return <span className="rounded-full px-2 py-0.5 text-[10px] h-serif tracking-wider uppercase" style={{ background: s.bg, color: 'var(--heritage-plum)' }}>{s.label}</span>;
}
